// @greenlight-spec: 07-source-control-pipeline.md#required-external-checks--the-orgs-own-scanners
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0057-org-authored-scanner-steps-isolated-job.md
import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useBusy } from '@/lib/useBusy';
import type { ExternalCheck, ReconcileReport } from '@greenlight/contract';

import { ExternalCheckRow } from './ExternalCheckRow';
import { SettingsSectionHeading } from './SettingsSectionHeading';

const KEY = ['policies', 'external-checks'];

/**
 * Settings → Policies: the checks an org requires from its own scanners
 * (docs/07 §Required external checks). Distinct from the shipped checks above —
 * Greenlight evaluates no rule here, it requires the result and relays it — so
 * the section states what it does rather than sitting unlabelled beside them.
 */
export function ExternalChecksSection(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = api.policies.listExternalChecks.useQuery(KEY, undefined, {
    queryKey: KEY,
  });
  const upsert = api.policies.upsertExternalCheck.useMutation();
  const remove = api.policies.removeExternalCheck.useMutation();

  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });

  // A pass that reached only some repos answers 200 with a report rather than an
  // error, so a success toast alone would tell IT the fleet is consistent when it is
  // not. Held in state as well as toasted: the repos are what IT has to act on, and
  // a toast is gone before they can read a list.
  const [unreconciled, setUnreconciled] = React.useState<ReconcileReport | null>(null);

  const reportOutcome = (report: ReconcileReport | undefined, successMessage: string): void => {
    if (report && report.failed > 0) {
      setUnreconciled(report);
      toast.warning(
        `Saved, but ${report.failed} ${report.failed === 1 ? 'repository' : 'repositories'} did not take the change.`,
      );
      return;
    }
    setUnreconciled(null);
    toast.success(successMessage);
  };

  const runUpsert = async (
    body: {
      check_name: string;
      github_app_id?: number;
      steps_yaml?: string;
      enforcement?: 'block' | 'warn';
      is_active?: boolean;
    },
    describe: (saved: ExternalCheck) => string,
  ): Promise<{ ok: boolean; message?: string; line?: number }> => {
    try {
      const res = await upsert.mutateAsync({ body });
      if (res.status >= 300) throw res;
      reportOutcome(res.body.reconcile, describe(res.body.check));
      await refresh();
      return { ok: true };
    } catch (err) {
      const bodyErr = (err as { body?: { message?: string; details?: { line?: number } } })?.body;
      const message = bodyErr?.message ?? "Couldn't save the check.";
      toast.error(message);
      return { ok: false, message, line: bodyErr?.details?.line };
    }
  };

  const runRemove = async (check: ExternalCheck): Promise<void> => {
    try {
      const res = await remove.mutateAsync({
        // Query, not a path segment: real check names carry slashes and parens.
        query: { check_name: check.check_name },
        body: {},
      });
      if (res.status >= 300) throw res;
      reportOutcome(res.body.reconcile, `${check.check_name} is no longer required.`);
      await refresh();
    } catch (err) {
      const message = (err as { body?: { message?: string } })?.body?.message;
      toast.error(message ?? "Couldn't remove the check.");
    }
  };

  // A 401/403/500 is a settled failure, not a pending load. Falling back to an empty list there would
  // render "no scanners named yet" over checks that are live and blocking — the one
  // wrong answer this section can give.
  const failed = isError || (!isLoading && data?.status !== 200);
  const checks = data?.status === 200 ? data.body.checks : [];

  return (
    <div data-testid="external-checks-section">
      <SettingsSectionHeading title="Your own scanners" pill="IT" />
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Already running CodeQL, Snyk, or another scanner? Name the check it posts and Greenlight
        will require it at merge and relay its findings to the coding agent — which never sees
        GitHub. Or paste the steps here and Greenlight will run them in every app&apos;s pipeline,
        in a job that cannot see Greenlight&apos;s secrets. A new check starts in warn so you can
        see what it reports before it gates.
      </p>

      {/* A branch rule that no longer matches the configuration is only fixable if IT
          can see which repository it is on, so the repos are named, not counted. */}
      {unreconciled !== null && (
        <Alert variant="destructive" className="mt-4" data-testid="external-checks-unreconciled">
          <AlertTitle>
            {unreconciled.failed} of {unreconciled.applied + unreconciled.failed} repositories
            didn&apos;t take this change
          </AlertTitle>
          <AlertDescription>
            <p>
              The check is saved, but branch protection on these repositories still reflects the
              previous setting. Re-saving the check retries them.
            </p>
            <ul className="mt-2 space-y-0.5">
              {unreconciled.failed_repos.map((repo) => (
                <li key={repo} className="font-mono text-[11.5px]">
                  {repo}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="mt-4 space-y-3" data-testid="external-checks-loading">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : failed ? (
        <Alert variant="destructive" className="mt-4" data-testid="external-checks-error">
          <AlertTitle>Couldn't load your scanners</AlertTitle>
          <AlertDescription>Something went wrong. Retry in a moment.</AlertDescription>
        </Alert>
      ) : (
        <div className="mt-4 space-y-3">
          {checks.length === 0 ? (
            <p
              className="rounded-gl-lg border border-border bg-card p-4 text-sm text-muted-foreground"
              data-testid="external-checks-empty"
            >
              No scanners named yet. Greenlight's own checks above still run on every commit.
            </p>
          ) : (
            checks.map((check) => (
              <ExternalCheckRow
                key={check.check_name}
                check={check}
                onUpdate={(body) =>
                  runUpsert(
                    {
                      check_name: check.check_name,
                      github_app_id: check.github_app_id,
                      ...body,
                    },
                    (saved) => `${saved.check_name} updated.`,
                  ).then(() => undefined)
                }
                onSaveSteps={
                  check.steps_yaml !== null
                    ? (yaml) =>
                        runUpsert(
                          { check_name: check.check_name, steps_yaml: yaml },
                          (saved) => `${saved.check_name} steps updated.`,
                        )
                    : undefined
                }
                onRemove={() => runRemove(check)}
              />
            ))
          )}
          <AddExternalCheck
            onAdd={(body) =>
              runUpsert(body, (saved) =>
                checks.some((existing) => existing.check_name === saved.check_name)
                  ? `${saved.check_name} updated. It still reports in ${saved.enforcement}.`
                  : `${saved.check_name} added, reporting in ${saved.enforcement}.`,
              ).then((r) => r.ok)
            }
          />
          <AddOrgScannerSteps
            onAdd={(body) =>
              runUpsert(
                body,
                (saved) =>
                  `${saved.check_name} added. Greenlight will run it in every app pipeline.`,
              )
            }
          />
        </div>
      )}
    </div>
  );
}

function AddExternalCheck({
  onAdd,
}: {
  onAdd: (body: { check_name: string; github_app_id: number }) => Promise<boolean>;
}): React.JSX.Element {
  const [name, setName] = React.useState('');
  const [appId, setAppId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, track] = useBusy();

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = name.trim();
    const parsed = Number(appId.trim());
    if (trimmed === '') {
      setError('Enter the check name exactly as the scanner posts it.');
      return;
    }
    // Rejected here as well as on the server: an app id that cannot match is a
    // check that can never go green, and the fix is far cheaper before it reaches
    // a branch rule than after.
    if (!Number.isInteger(parsed) || parsed <= 0) {
      setError('The GitHub App id is a positive number, found on the app’s settings page.');
      return;
    }
    setError(null);
    if (await track(() => onAdd({ check_name: trimmed, github_app_id: parsed }))) {
      setName('');
      setAppId('');
    }
  };

  return (
    <form
      className="rounded-gl-lg border border-dashed border-border bg-card p-4"
      onSubmit={(e) => void submit(e)}
      aria-busy={busy}
      data-testid="external-check-add"
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="external-check-name">Check name</Label>
          <Input
            id="external-check-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="CodeQL"
            disabled={busy}
            className="h-11 md:h-9"
          />
        </div>
        <div className="space-y-1.5 md:w-48">
          <Label htmlFor="external-check-app-id">GitHub App id</Label>
          <Input
            id="external-check-app-id"
            inputMode="numeric"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            placeholder="57789"
            disabled={busy}
            className="h-11 md:h-9"
          />
        </div>
        <Button type="submit" disabled={busy} className="h-11 md:h-9">
          {busy ? (
            <>
              <Loader2 className="size-4 motion-safe:animate-spin" aria-hidden />
              Adding…
            </>
          ) : (
            'Add check'
          )}
        </Button>
      </div>
      {error !== null && (
        <p role="alert" className="mt-2 text-[13px] text-gl-red-text">
          {error}
        </p>
      )}
    </form>
  );
}

function AddOrgScannerSteps({
  onAdd,
}: {
  onAdd: (body: {
    check_name: string;
    steps_yaml: string;
  }) => Promise<{ ok: boolean; message?: string; line?: number }>;
}): React.JSX.Element {
  const [name, setName] = React.useState('');
  const [yaml, setYaml] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, track] = useBusy();

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Name the check this job will post — it is what merge protection will require.');
      return;
    }
    if (yaml.trim() === '') {
      setError('Paste a YAML list of GitHub Actions steps.');
      return;
    }
    const result = await track(() => onAdd({ check_name: trimmed, steps_yaml: yaml }));
    if (result.ok) {
      setName('');
      setYaml('');
      setError(null);
      return;
    }
    setError(
      result.line !== undefined
        ? `Line ${result.line}: ${result.message}`
        : (result.message ?? null),
    );
  };

  return (
    <form
      className="rounded-gl-lg border border-dashed border-border bg-card p-4"
      onSubmit={(e) => void submit(e)}
      aria-busy={busy}
      data-testid="org-scanner-steps-add"
    >
      <p className="text-[13px] font-medium text-foreground">Paste steps Greenlight runs</p>
      <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
        A YAML list of steps — not a workflow file. They run in the org&apos;s repos on
        GitHub-hosted runners and cost CI minutes. Greenlight schedules them; it does not vouch for
        them.
      </p>
      <div className="mt-3 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="org-scanner-name">Check name</Label>
          <Input
            id="org-scanner-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="CodeQL Analyze"
            disabled={busy}
            className="h-11 md:h-9"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="org-scanner-yaml">Steps</Label>
          <Textarea
            id="org-scanner-yaml"
            value={yaml}
            onChange={(e) => setYaml(e.target.value)}
            spellCheck={false}
            placeholder={'- name: Initialize CodeQL\n  uses: github/codeql-action/init@v3'}
            disabled={busy}
            className="min-h-32 font-mono text-[13px]"
          />
        </div>
        <Button type="submit" disabled={busy} className="h-11 md:h-9">
          {busy ? (
            <>
              <Loader2 className="size-4 motion-safe:animate-spin" aria-hidden />
              Adding…
            </>
          ) : (
            'Add steps'
          )}
        </Button>
      </div>
      {error !== null && (
        <p role="alert" className="mt-2 text-[13px] text-gl-red-text">
          {error}
        </p>
      )}
    </form>
  );
}
